import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import TESTIDS from '@/constants/testIds';

export default function InboxPage() {
    const { brandId, accountId, brands } = useScope();
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [drafting, setDrafting] = useState(false);
    const [editing, setEditing] = useState('');
    const [activeDraftId, setActiveDraftId] = useState(null);

    const loadThreads = async () => {
        const params = {};
        if (brandId) params.brand_id = brandId;
        if (accountId) params.account_id = accountId;
        const r = await apiClient.get('/inbox/dms/threads', { params });
        setThreads(r.data);
        if (r.data.length && !activeId) setActiveId(r.data[0].id);
    };

    useEffect(() => {
        loadThreads();
    }, [brandId, accountId]);

    useEffect(() => {
        if (!activeId) {
            setThread(null);
            setMessages([]);
            setEditing('');
            setActiveDraftId(null);
            return;
        }
        apiClient.get(`/inbox/dms/threads/${activeId}`).then((r) => {
            setThread(r.data.thread);
            setMessages(r.data.messages);
            const lastDraft = [...(r.data.messages || [])]
                .reverse()
                .find((m) => m.direction === 'out' && m.status === 'draft');
            setActiveDraftId(lastDraft?.id || null);
            setEditing(lastDraft?.body || '');
        });
    }, [activeId]);

    const draft = async () => {
        if (!thread) return;
        setDrafting(true);
        try {
            const r = await apiClient.post(`/inbox/dms/threads/${thread.id}/draft_reply`);
            const fresh = await apiClient.get(`/inbox/dms/threads/${thread.id}`);
            setMessages(fresh.data.messages);
            setActiveDraftId(r.data.id);
            setEditing(r.data.body || '');
            toast.success('DM reply drafted');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'DM draft failed');
        } finally {
            setDrafting(false);
        }
    };

    const send = async () => {
        if (!activeDraftId) {
            toast.error('No DM draft yet');
            return;
        }
        try {
            await apiClient.patch(`/inbox/dms/messages/${activeDraftId}`, { draft_text: editing, status: 'sent' });
            toast.success('DM marked as sent (placeholder)');
            const fresh = await apiClient.get(`/inbox/dms/threads/${thread.id}`);
            setMessages(fresh.data.messages);
            setActiveDraftId(null);
            setEditing('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send');
        }
    };

    return (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_1fr]">
            <Card className="border-border bg-card">
                <div className="border-b border-border p-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{threads.length} threads</div>
                    <h2 className="text-base font-semibold">Direct messages</h2>
                </div>
                <ul className="divide-y divide-border">
                    {threads.length === 0 ? (
                        <li className="p-6 text-sm text-muted-foreground">No DMs in this scope.</li>
                    ) : null}
                    {threads.map((t) => {
                        const brand = brands.find((b) => b.id === t.brand_id);
                        const accent = brandColorVar(brand?.color_slot);
                        return (
                            <li
                                key={t.id}
                                data-testid={TESTIDS.dmThreadRow}
                                onClick={() => setActiveId(t.id)}
                                className={`cursor-pointer px-4 py-3 transition-colors duration-150 hover:bg-[hsl(var(--surface-2))] ${
                                    activeId === t.id ? 'bg-[hsl(var(--surface-2))]' : ''
                                }`}
                                style={{ '--brand-accent-current': accent }}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${accent})` }} />
                                    <span className="font-mono text-[12px]">@{t.participant_handle}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground">on @{t.account_handle}</div>
                                <div className="mt-1 text-[10px] text-muted-foreground">last at {new Date(t.last_message_at).toLocaleString()}</div>
                            </li>
                        );
                    })}
                </ul>
            </Card>
            <Card className="border-border bg-card p-4">
                {!thread ? (
                    <div className="text-sm text-muted-foreground">Select a thread to view and draft a safe reply.</div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-mono text-sm">@{thread.participant_handle}</div>
                                <div className="text-[11px] text-muted-foreground">on @{thread.account_handle}</div>
                            </div>
                        </div>
                        <div className="max-h-[300px] space-y-2 overflow-y-auto rounded-md border border-border bg-[hsl(var(--surface-2))] p-3">
                            {messages.map((m) => (
                                <div
                                    key={m.id}
                                    className={`max-w-[80%] rounded-md p-2 text-sm ${
                                        m.direction === 'in'
                                            ? 'bg-[hsl(var(--surface-3))] text-foreground'
                                            : 'ml-auto bg-[hsl(var(--primary)/0.18)] text-foreground'
                                    }`}
                                >
                                    <div>{m.body}</div>
                                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                                        {m.direction === 'in' ? 'in' : `out · ${m.status}`} • {new Date(m.created_at).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" onClick={draft} disabled={drafting} data-testid={TESTIDS.dmDraftReplyButton}>
                                <Sparkles className="mr-1 h-3 w-3" /> {drafting ? 'Drafting…' : 'Draft AI reply'}
                            </Button>
                        </div>
                        <div>
                            <Textarea rows={4} value={editing} onChange={(e) => setEditing(e.target.value)} data-testid={TESTIDS.dmReplyEdit} />
                            <div className="mt-2 flex justify-end">
                                <Button size="sm" onClick={send} disabled={!activeDraftId} data-testid={TESTIDS.dmReplySendButton}>
                                    <Send className="mr-1 h-3 w-3" /> Mark as sent
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
